import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import type {
  AdjustStockRefusal,
  FulfillmentAdminClient,
  InventoryRow,
  ManualStockDirection,
} from '@/infrastructure/FulfillmentAdminClient';
import { FulfillmentSound } from '@/pages/fulfillment/components/outboundSound';
import { CameraScanField } from '@/pages/fulfillment/components/CameraScanField';
import { normalizeLocationCode } from '@/pages/fulfillment/inventory/inventoryView';
import { ROUTES } from '@/routes/routes';
import { stockSkuFormatError, useStockSkuValidation, type StockSkuLookup } from '../stockSkuValidation';
import { adjustStockRefusalOf, describeFailure } from '@/utils/fulfillmentErrors';

export interface ManualStockFormProps {
  /** `listInventory` reads where the entered sku is stocked, fresh, rather than from the page's capped list. */
  adminClient: Pick<FulfillmentAdminClient, 'adjustStock' | 'listInventory'>;
  stockClient: StockSkuLookup;
  onRecorded: (message: string) => void;
  onFailure: (message: string) => void;
}

/** The facade's reason limit; Stock History shows the reason as typed. */
export const MANUAL_STOCK_REASON_MAX = 500;

const DIRECTIONS: Array<{ value: ManualStockDirection; label: string }> = [
  { value: 'DECREASE', label: 'Decrease — outbound / short' },
  { value: 'INCREASE', label: 'Increase — count found over' },
];

/**
 * V1's one-bucket warehouse. Until each sku is moved onto its shelves its V2 stock still sits here, so a
 * decrease typed against the real shelf finds nothing to take, and an increase there counts the units twice.
 */
export const HOLDING_LOCATION = 'WH#00';

/** How long the sku box stays still before its stock is read, so a typed sku is not read once per key. */
export const SKU_STOCK_READ_DELAY_MS = 250;

/** How long a refusal waits for the stock read its WH#00 guidance needs before it shows without it. */
export const REFUSAL_STOCK_READ_TIMEOUT_MS = 3_000;

/** How long the sku's stock read may take before it counts as failed. */
export const SKU_STOCK_READ_TIMEOUT_MS = 10_000;

/** One sku's stocked locations as last read; `rows` is null when the read failed. */
interface SkuStock {
  sku: string;
  generation: number;
  rows: InventoryRow[] | null;
}

/** What to do about a refusal, in both of the warehouse's working languages. */
export interface RefusalGuidance {
  en: string;
  ko: string;
}

/** A refusal as the form shows it: the server's reason, then what to do about it when the form can tell. */
interface ShownRefusal {
  message: string;
  guidance: RefusalGuidance | null;
}

const holdingRowOf = (rows: InventoryRow[] | null | undefined): InventoryRow | undefined =>
  rows?.find((row) => normalizeLocationCode(row.locationCode) === HOLDING_LOCATION && row.onHand > 0);

const stockLine = (row: InventoryRow): string =>
  `${row.locationCode}: ${row.onHand}${row.reserved > 0 ? ` (${row.reserved} reserved)` : ''}`;

/** A decrease refused because the shelf typed, other than WH#00 itself, holds none of the sku. */
const isEmptyShelfRefusal = (refusal: AdjustStockRefusal): boolean =>
  refusal.code === 'INSUFFICIENT_STOCK' &&
  refusal.scope === 'SHELF' &&
  refusal.onHand === 0 &&
  normalizeLocationCode(refusal.locationCode) !== HOLDING_LOCATION;

/**
 * An empty-shelf refusal of a sku whose stock is still at WH#00: the server is right, but its reason alone
 * reads as a broken form, so the operator is told where the stock is booked and how much of it can go.
 */
export const describeHoldingGuidance = (
  refusal: AdjustStockRefusal,
  holding: InventoryRow | undefined,
): RefusalGuidance | null => {
  if (refusal.code !== 'INSUFFICIENT_STOCK' || !isEmptyShelfRefusal(refusal) || !holding) {
    return null;
  }
  const takeable = Math.max(0, holding.onHand - holding.reserved);
  const reservedEn =
    holding.reserved === 0
      ? ''
      : takeable === 0
        ? ` All of it is reserved for orders, and reserved stock cannot be taken by hand.`
        : ` Stock reserved for orders cannot be taken, so at most ${takeable} can be taken there.`;
  const reservedKo =
    holding.reserved === 0
      ? ''
      : takeable === 0
        ? ' 전부 주문에 예약되어 있고, 예약된 수량은 뺄 수 없습니다.'
        : ` 주문에 예약된 수량은 뺄 수 없어 최대 ${takeable}개까지 뺄 수 있습니다.`;
  return {
    en: `${refusal.sku} is still booked at ${HOLDING_LOCATION} (${holding.onHand} on hand, ${holding.reserved} reserved), not on a shelf yet — enter ${HOLDING_LOCATION} as the location.${reservedEn}`,
    ko: `이 SKU 재고는 아직 선반이 아니라 ${HOLDING_LOCATION}에 있습니다(${holding.onHand}개, 예약 ${holding.reserved}개). Location에 ${HOLDING_LOCATION}을 입력하세요.${reservedKo}`,
  };
};

/** The shelf or the whole sku, whichever fell short, as the refusal names it. */
const scopeOf = (refusal: Extract<AdjustStockRefusal, { scope: 'SHELF' | 'SKU' }>): string =>
  refusal.scope === 'SHELF'
    ? `${refusal.locationCode} holds ${refusal.onHand} of ${refusal.sku}`
    : `${refusal.sku} holds ${refusal.onHand} across all shelves`;

/** Each refusal in the operator's terms, with the figures that decide what can still be taken. */
export const describeAdjustStockRefusal = (refusal: AdjustStockRefusal, qty: number): string => {
  switch (refusal.code) {
    case 'MANUAL_STOCK_BEFORE_LAUNCH':
      return 'Not recorded — Fulfillment 2.0 is not live yet. Reload the page to record this on the current stock system.';
    case 'INSUFFICIENT_STOCK':
      return `Not recorded — not enough stock to take ${qty}: ${scopeOf(refusal)}${
        refusal.reserved > 0 ? `, ${refusal.reserved} reserved for orders` : ''
      }.`;
    case 'RESERVED_STOCK':
      return `Not recorded — stock reserved for an order cannot leave by hand: ${scopeOf(refusal)}, ${
        refusal.reserved
      } reserved for orders, so at most ${Math.max(0, refusal.onHand - refusal.reserved)} can be taken.`;
    case 'LOCATION_OCCUPIED':
      return `Not recorded — ${refusal.locationCode} already holds ${refusal.occupiedBy}; one shelf holds one product.`;
  }
};

/**
 * The Outbound tab once Fulfillment 2.0 is live (SSD-7099): a manual outbound or a correction at one shelf,
 * booked on the V2 stock ledger with its reason and the signed-in operator. The server refuses taking stock
 * that is not there or is reserved for an order; the refusal stays on the form until the operator edits it.
 */
export const ManualStockForm = ({ adminClient, stockClient, onRecorded, onFailure }: ManualStockFormProps) => {
  const [direction, setDirection] = useState<ManualStockDirection>('DECREASE');
  const [sku, setSku] = useState('');
  const [locationCode, setLocationCode] = useState('');
  const [qty, setQty] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<ShownRefusal | null>(null);
  const [stock, setStock] = useState<SkuStock | null>(null);
  const stockGenerationRef = useRef(0);
  // One key per entered change: a retry of the same draft replays instead of moving the stock twice,
  // and any edit makes it a different change that needs its own key.
  const keyRef = useRef(uuidv4());

  const edit =
    <T,>(set: (value: T) => void) =>
    (value: T) => {
      set(value);
      keyRef.current = uuidv4();
      setRefusal(null);
    };

  const quantity = Number(qty.trim());
  const validQty = qty.trim() !== '' && Number.isInteger(quantity) && quantity > 0;
  const trimmedSku = sku.trim();
  const skuValidation = useStockSkuValidation(stockClient, sku);

  const showStock = (read: SkuStock) => {
    if (read.generation === stockGenerationRef.current) {
      setStock(read);
    }
  };

  // A stalled read counts as a failed one, so it can never hold Record Increase shut.
  const readStock = async (target: string, generation: number): Promise<SkuStock> => {
    let timer: number | undefined;
    try {
      const { rows } = await Promise.race([
        adminClient.listInventory({ skus: [target] }),
        new Promise<never>((_, reject) => {
          timer = window.setTimeout(() => reject(new Error('Inventory read timed out.')), SKU_STOCK_READ_TIMEOUT_MS);
        }),
      ]);
      return { sku: target, generation, rows: rows.filter((row) => row.sku === target && row.locationCode) };
    } catch {
      return { sku: target, generation, rows: null };
    } finally {
      window.clearTimeout(timer);
    }
  };

  // The refusal waits only so long for its guidance; a read that answers later still refreshes the hint and
  // the Increase warning, but too late to stand behind the refusal.
  const readRefusalStock = async (target: string, generation: number): Promise<SkuStock> =>
    new Promise((resolve) => {
      let timedOut = false;
      const timer = window.setTimeout(() => {
        timedOut = true;
        resolve({ sku: target, generation, rows: null });
      }, REFUSAL_STOCK_READ_TIMEOUT_MS);
      void readStock(target, generation).then((read) => {
        window.clearTimeout(timer);
        if (timedOut) {
          showStock(read);
        } else {
          resolve(read);
        }
      });
    });

  useEffect(() => {
    if (stockSkuFormatError(trimmedSku)) {
      return;
    }
    const generation = stockGenerationRef.current;
    const timer = window.setTimeout(() => {
      void readStock(trimmedSku, generation).then(showStock);
    }, SKU_STOCK_READ_DELAY_MS);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sku]);

  /** `undefined` while the entered sku's read is in flight, null once it failed. */
  const shelves = stock?.generation === stockGenerationRef.current && stock.sku === trimmedSku ? stock.rows : undefined;
  const ranked = shelves ? [...shelves].sort((a, b) => b.onHand - a.onHand) : [];
  const holding = holdingRowOf(shelves);
  const ready =
    skuValidation.valid &&
    locationCode.trim() !== '' &&
    validQty &&
    reason.trim() !== '' &&
    !busy &&
    (direction !== 'INCREASE' || shelves !== undefined);

  const record = async () => {
    if (!ready) {
      return;
    }
    const recordGeneration = stockGenerationRef.current;
    const recordKey = keyRef.current;
    setBusy(true);
    setRefusal(null);
    try {
      const result = await adminClient.adjustStock({
        sku: trimmedSku,
        // The server normalises only an INCREASE; a DECREASE matches the shelf as sent, so both are sent normalised.
        locationCode: normalizeLocationCode(locationCode),
        direction,
        qty: quantity,
        reason: reason.trim(),
        idempotencyKey: recordKey,
      });
      FulfillmentSound.success();
      const change = `${result.sku} ${result.delta > 0 ? '+' : '−'}${Math.abs(result.delta)} at ${result.locationCode}`;
      onRecorded(
        result.replayed
          ? `Already recorded: ${change} — nothing moved again.`
          : `Stock ${result.delta > 0 ? 'increase' : 'decrease'} recorded: ${change}.`,
      );
      stockGenerationRef.current += 1;
      setStock(null);
      setSku('');
      setLocationCode('');
      setQty('');
      setReason('');
      setDirection('DECREASE');
      keyRef.current = uuidv4();
    } catch (error) {
      FulfillmentSound.failure();
      const coded = adjustStockRefusalOf(error);
      const message = coded
        ? describeAdjustStockRefusal(coded, quantity)
        : describeFailure(error, 'Could not record the stock change.');
      // The guidance quotes WH#00's figures, so they are read again rather than taken from the hint's older read.
      let guidance: RefusalGuidance | null = null;
      let guidanceGeneration = recordGeneration;
      let showRefusal = recordGeneration === stockGenerationRef.current && recordKey === keyRef.current;
      if (coded && isEmptyShelfRefusal(coded) && showRefusal) {
        guidanceGeneration = ++stockGenerationRef.current;
        setStock(null);
        const fresh = await readRefusalStock(trimmedSku, guidanceGeneration);
        showStock(fresh);
        guidance = describeHoldingGuidance(coded, holdingRowOf(fresh.rows));
        showRefusal = guidanceGeneration === stockGenerationRef.current && recordKey === keyRef.current;
      }
      if (showRefusal) {
        setRefusal({ message, guidance });
      }
      onFailure(showRefusal && guidance ? `${message} ${guidance.en} ${guidance.ko}` : message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="form-note">
        Record stock that leaves the warehouse outside an order (sample · damage · disposal · B2B), or correct a
        miscount, on Fulfillment 2.0. Enter the location as Current Stocks shows it. Stock reserved for an order cannot
        be taken by hand, and an increase must go onto a real shelf.
      </div>
      <div className="formgrid">
        <div className="fld">
          <label htmlFor="manual-stock-direction">Change *</label>
          <select
            id="manual-stock-direction"
            aria-label="Stock change"
            value={direction}
            onChange={(event) => edit(setDirection)(event.target.value as ManualStockDirection)}
          >
            {DIRECTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="fld">
          <label htmlFor="manual-stock-sku">SKU *</label>
          <CameraScanField label="Scan outbound SKU with camera">
            <input
              id="manual-stock-sku"
              aria-label="Outbound SKU"
              aria-describedby="manual-stock-sku-validation"
              aria-invalid={skuValidation.invalid}
              placeholder="100004819"
              value={sku}
              onChange={(event) => {
                stockGenerationRef.current += 1;
                setStock(null);
                edit(setSku)(event.target.value);
              }}
            />
          </CameraScanField>
          <div id="manual-stock-sku-validation" className="form-hint" aria-live="polite">
            {skuValidation.message}
            {skuValidation.canRetry && (
              <button type="button" onClick={skuValidation.retry}>
                Retry SKU lookup
              </button>
            )}
          </div>
        </div>
        <div className="fld">
          <label htmlFor="manual-stock-location">Location *</label>
          <CameraScanField label="Scan outbound location with camera">
            <input
              id="manual-stock-location"
              aria-label="Outbound location"
              placeholder="A-02-13"
              list="manual-stock-shelves"
              value={locationCode}
              onChange={(event) => edit(setLocationCode)(event.target.value)}
            />
          </CameraScanField>
          <datalist id="manual-stock-shelves">
            {ranked.map((row) => (
              <option key={row.locationCode} value={row.locationCode}>
                {`${row.onHand} on hand · ${row.reserved} reserved`}
              </option>
            ))}
          </datalist>
          {!stockSkuFormatError(trimmedSku) && (
            // Where the stock is booked, before anything is typed here: a shelf with none of it refuses a decrease.
            <div className="form-hint" aria-label="SKU stock locations">
              {shelves === undefined
                ? `Reading where ${trimmedSku} is stocked…`
                : shelves === null
                  ? `Could not read where ${trimmedSku} is stocked.`
                  : ranked.length === 0
                    ? `${trimmedSku} has no stock on Fulfillment 2.0.`
                    : `${trimmedSku} stock — ${ranked.map(stockLine).join(' · ')}`}
            </div>
          )}
        </div>
        <div className="fld">
          <label htmlFor="manual-stock-qty">Quantity *</label>
          <input
            id="manual-stock-qty"
            aria-label="Outbound quantity"
            type="number"
            min={1}
            step={1}
            placeholder="0"
            value={qty}
            onChange={(event) => edit(setQty)(event.target.value)}
          />
        </div>
        <div className="fld fld-wide">
          <label htmlFor="manual-stock-reason">Reason *</label>
          <input
            id="manual-stock-reason"
            aria-label="Outbound reason"
            placeholder="Sample for influencer, damaged box, B2B order…"
            maxLength={MANUAL_STOCK_REASON_MAX}
            value={reason}
            onChange={(event) => edit(setReason)(event.target.value)}
          />
        </div>
      </div>
      {direction === 'INCREASE' && holding && (
        // An increase adds units; one typed for stock that only changed shelves books those units a second time.
        <div className="form-warning" aria-label="Increase warning">
          <div>
            {`${trimmedSku} still has ${holding.onHand} at ${HOLDING_LOCATION}. If you are only putting those units on a shelf, do not record an Increase — use `}
            <Link to={ROUTES.fulfillment.stockMove}>Stock Move</Link>
            {` from ${HOLDING_LOCATION} instead. An Increase adds new stock, so the same units would be counted twice.`}
          </div>
          <div lang="ko">
            {`이 SKU는 아직 ${HOLDING_LOCATION}에 ${holding.onHand}개가 있습니다. 물건 위치만 바꾸는 거라면 Increase 말고 `}
            <Link to={ROUTES.fulfillment.stockMove}>Stock Move</Link>
            {`로 ${HOLDING_LOCATION}에서 옮기세요. Increase는 재고를 새로 더하므로 같은 물건이 두 번 계산됩니다.`}
          </div>
        </div>
      )}
      {refusal && (
        // The toast announces the refusal; this copy stays beside the fields until the operator edits them.
        <div className="form-refusal" aria-label="Stock change refusal">
          <div>{refusal.message}</div>
          {refusal.guidance && (
            <>
              <div>{refusal.guidance.en}</div>
              <div lang="ko">{refusal.guidance.ko}</div>
            </>
          )}
        </div>
      )}
      <button type="button" className="btn btn-blue" disabled={!ready} onClick={() => void record()}>
        {direction === 'INCREASE' ? 'Record Increase' : 'Record Outbound'}
      </button>
    </div>
  );
};
